import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { TreePanel } from '../components/schema/TreePanel';
import { EendraadCanvas } from '../components/schema/EendraadCanvas';
import { SituatiePlan } from '../components/schema/SituatiePlan';
import { ArrowLeft, Save, FileDown, FolderOpen, Loader2, Undo2, Eye, PenLine, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { KOEN_SIGNATURE, KOEN_NAME } from '../components/schema/defaultSignature';
import { KeuringStatusControl } from '../components/KeuringStatusControl';

// Tekenvlak voor handtekening
const SignPad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = 160 * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1e293b';
  }, []);
  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const down = (e) => { e.preventDefault(); canvasRef.current.setPointerCapture(e.pointerId); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); onChange(canvasRef.current.toDataURL('image/png')); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext('2d'); ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height); ctx.restore(); onChange(null); };
  return (
    <div>
      <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        style={{ touchAction: 'none' }} className="w-full h-[160px] bg-white border-2 border-dashed border-border rounded-lg cursor-crosshair"
        data-testid="schema-signature-canvas" />
      <div className="flex justify-end mt-1">
        <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="schema-signature-clear">Wissen</Button>
      </div>
    </div>
  );
};

// SVG element → PNG base64 (voor PDF export)
async function svgToPng(svgEl, scale = 2) {
  const clone = svgEl.cloneNode(true);
  const panRoot = clone.querySelector('#pan-root');
  if (panRoot) panRoot.setAttribute('transform', '');
  const vb = svgEl.viewBox.baseVal;
  clone.setAttribute('width', vb.width * scale);
  clone.setAttribute('height', vb.height * scale);
  const xml = new XMLSerializer().serializeToString(clone);
  const svg64 = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = vb.width * scale;
      canvas.height = vb.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = svg64;
  });
}

export default function SchemaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigImage, setSigImage] = useState(null);
  const [sigName, setSigName] = useState('');
  const [sigDate, setSigDate] = useState(new Date().toISOString().slice(0, 10));
  const [dirty, setDirty] = useState(false);
  const [products, setProducts] = useState([]);
  const [companyMeta, setCompanyMeta] = useState(null);
  const historyRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const eendraadRef = useRef(null);
  const situatieRef = useRef(null);
  const canvasWrapRef = useRef(null);

  useEffect(() => {
    if (schema && canvasWrapRef.current) {
      canvasWrapRef.current.scrollTop = canvasWrapRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!schema]);

  useEffect(() => {
    api.get('/installation-products').then(r => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/company-settings').then(r => setCompanyMeta(r.data || {})).catch(() => {});
  }, []);

  const pushHistory = useCallback((s) => {
    historyRef.current.push({ tree: s.tree, situation: s.situation });
    if (historyRef.current.length > 50) historyRef.current.shift();
    setCanUndo(true);
  }, []);

  const handleUndo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (prev) {
      setSchema(s => ({ ...s, tree: prev.tree, situation: prev.situation }));
      setDirty(true);
    }
    setCanUndo(historyRef.current.length > 0);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleUndo]);

  useEffect(() => {
    api.get(`/schemas/${id}`)
      .then(r => setSchema(r.data))
      .catch(() => { toast.error('Schema niet gevonden'); navigate('/schemas'); });
  }, [id, navigate]);

  const setTree = useCallback((tree) => { setSchema(s => { pushHistory(s); return { ...s, tree }; }); setDirty(true); }, [pushHistory]);
  const setSituation = useCallback((situation) => { setSchema(s => { pushHistory(s); return { ...s, situation }; }); setDirty(true); }, [pushHistory]);

  const deleteConsumer = useCallback((diffId, circuitId, idx) => {
    setSchema(s => {
      pushHistory(s);
      return {
        ...s,
        tree: {
          ...s.tree,
          differentials: (s.tree?.differentials || []).map(d => d.id !== diffId ? d : {
            ...d,
            circuits: (d.circuits || []).map(c => c.id !== circuitId ? c : { ...c, consumers: (c.consumers || []).filter((_, i) => i !== idx) })
          })
        }
      };
    });
    setDirty(true);
    toast.success('Symbool verwijderd (Ctrl+Z = ongedaan maken)');
  }, [pushHistory]);

  const onHideKwh = useCallback((target) => {
    setSchema(s => {
      pushHistory(s);
      if (target === 'main') return { ...s, tree: { ...s.tree, show_kwh_main: false } };
      return {
        ...s,
        tree: {
          ...s.tree,
          differentials: (s.tree?.differentials || []).map(d => d.id !== target.d ? d : {
            ...d, circuits: (d.circuits || []).map(c => c.id !== target.c ? c : { ...c, show_kwh: false })
          })
        }
      };
    });
    setDirty(true);
    toast.success('kWh-meter verwijderd (Ctrl+Z = ongedaan maken)');
  }, [pushHistory]);

  // symbolen uit het eendraadschema automatisch in het situatieplan plaatsen (eenmalig per type)
  useEffect(() => {
    if (!schema?.tree) return;
    const diffs = schema.tree.differentials || [];
    if (!diffs.length) return;
    const circuits = diffs.flatMap(d => d.circuits || []);
    const needed = ['verdeelbord', 'kwh_meter', 'aarding'];
    if (circuits.some(c => c.kind === 'pv')) needed.push('omvormer');
    if (circuits.some(c => c.kind === 'batterij')) needed.push('batterij');
    if (circuits.some(c => c.kind === 'laadpaal' || (c.subcircuits || []).some(sc => sc.kind === 'laadpaal') || (c.consumers || []).some(co => co.type === 'laadpaal'))) needed.push('laadpaal');
    const placed = schema.situation?.auto_placed || [];
    const toAdd = needed.filter(t => !placed.includes(t));
    const SYNCED = ['omvormer', 'batterij', 'laadpaal'];
    const toRemove = placed.filter(t => SYNCED.includes(t) && !needed.includes(t));
    if (!toAdd.length && !toRemove.length) return;
    const POS = { verdeelbord: [400, 280], kwh_meter: [400, 370], aarding: [400, 460], omvormer: [570, 280], batterij: [570, 420], laadpaal: [730, 280] };
    const newItems = toAdd.map(t => ({ id: Math.random().toString(36).slice(2, 10), symbol: t, x: POS[t][0], y: POS[t][1], rotation: 0, circuit_no: '', label: '', auto: true }));
    setSchema(s => {
      const sit = s.situation || {};
      const items = [...(sit.items || [])];
      toRemove.forEach(t => {
        let idx = items.findIndex(i => i.symbol === t && i.auto);
        if (idx < 0) idx = items.findIndex(i => i.symbol === t);
        if (idx >= 0) items.splice(idx, 1);
      });
      return {
        ...s,
        situation: {
          ...sit,
          items: [...items, ...newItems],
          auto_placed: [...placed.filter(t => !toRemove.includes(t)), ...toAdd]
        }
      };
    });
    setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema?.tree]);

  const handleSave = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/schemas/${id}`, { tree: schema.tree, situation: schema.situation, name: schema.name, signature: schema.signature || undefined });
      setDirty(false);
      if (!silent) toast.success('Schema opgeslagen');
    } catch (e) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const saveSignature = async () => {
    if (!sigImage) { toast.error('Teken eerst een handtekening'); return; }
    const sig = { image: sigImage, name: sigName, date: sigDate };
    setSchema(s => ({ ...s, signature: sig }));
    try {
      await api.put(`/schemas/${id}`, { signature: sig });
      toast.success('Handtekening geplaatst');
      setSigOpen(false);
    } catch { toast.error('Kon handtekening niet opslaan'); }
  };

  const removeSignature = async () => {
    setSchema(s => ({ ...s, signature: null }));
    try {
      await api.put(`/schemas/${id}`, { signature: {} });
      toast.success(`Standaard handtekening van ${KOEN_NAME} hersteld`);
      setSigOpen(false);
    } catch { toast.error('Kon handtekening niet verwijderen'); }
  };

  const handleExport = async (pushToSharepoint = false) => {
    setExporting(true);
    try {
      await handleSave(true);
      const eendraadPng = eendraadRef.current ? await svgToPng(eendraadRef.current) : null;
      // Altijd beide tekeningen meesturen: PDF bevat telkens eendraadschema én situatieplan
      const situatiePng = situatieRef.current ? await svgToPng(situatieRef.current) : null;
      const res = await api.post(`/schemas/${id}/export-pdf`, {
        eendraad_png: eendraadPng,
        situatie_png: situatiePng,
        push_to_sharepoint: pushToSharepoint
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${schema.name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(pushToSharepoint ? 'PDF gedownload én naar klantmap gepusht' : 'PDF gedownload');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export mislukt');
    } finally {
      setExporting(false);
    }
  };

  const handlePreview = async () => {
    setExporting(true);
    try {
      await handleSave(true);
      const eendraadPng = eendraadRef.current ? await svgToPng(eendraadRef.current) : null;
      const situatiePng = situatieRef.current ? await svgToPng(situatieRef.current) : null;
      const res = await api.post(`/schemas/${id}/export-pdf`, {
        eendraad_png: eendraadPng,
        situatie_png: situatiePng,
        push_to_sharepoint: false
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF-controle mislukt');
    } finally {
      setExporting(false);
    }
  };

  if (!schema) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const allCircuits = (schema.tree?.differentials || []).flatMap(d => d.circuits || []);

  const meta = {
    customerName: schema.customer_name || '',
    address: schema.property_address || '',
    netType: schema.tree?.supply?.voltage || '230V',
    company: {
      name: companyMeta?.company_name || '',
      address: companyMeta?.address || '',
      phone: companyMeta?.phone || '',
      email: companyMeta?.email || '',
      vat: companyMeta?.vat_number || '',
    },
  };

  return (
    <div className="space-y-4" data-testid="schema-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/schemas')} data-testid="back-to-schemas">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <input value={schema.name} onChange={e => { setSchema(s => ({ ...s, name: e.target.value })); setDirty(true); }}
              className="text-xl font-bold bg-transparent border-none outline-none focus:ring-0 w-full min-w-[200px]"
              data-testid="schema-title-input" />
            <p className="text-xs text-muted-foreground truncate">
              {schema.customer_name || 'Geen klant'} {schema.property_address ? `· ${schema.property_address}` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleUndo} disabled={!canUndo} title="Ongedaan maken (Ctrl+Z)" data-testid="undo-btn">
            <Undo2 className="w-4 h-4 mr-1.5" /> Vorige
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving} data-testid="save-schema-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Opslaan{dirty ? ' *' : ''}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={exporting} title="Open de PDF ter controle in een nieuw tabblad" data-testid="preview-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
            Controleer PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setSigImage(null); setSigName(schema.signature?.name || ''); setSigDate(schema.signature?.date || new Date().toISOString().slice(0, 10)); setSigOpen(true); }} data-testid="signature-btn">
            <PenLine className="w-4 h-4 mr-1.5" /> Handtekening ✓
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport(false)} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
            Export PDF
          </Button>
          {schema.customer_id && (
            <Button size="sm" onClick={() => handleExport(true)} disabled={exporting} data-testid="push-schema-sharepoint-btn">
              <FolderOpen className="w-4 h-4 mr-1.5" /> PDF + naar klantmap
            </Button>
          )}
          <KeuringStatusControl installation={schema} endpoint={`/schemas/${schema.id}/keuring`} size="md"
            onUpdated={(kr) => setSchema(s => ({ ...s, keuring_request: kr }))} />
        </div>
      </div>

      <Tabs defaultValue="eendraad">
        <TabsList>
          <TabsTrigger value="eendraad" data-testid="tab-eendraad">Eendraadschema</TabsTrigger>
          <TabsTrigger value="situatie" data-testid="tab-situatie">Situatieplan</TabsTrigger>
        </TabsList>

        <TabsContent value="eendraad" forceMount className="mt-4 data-[state=inactive]:hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 max-h-[75vh] overflow-y-auto pr-1">
              <TreePanel tree={schema.tree} onChange={setTree} products={products} />
            </div>
            <div ref={canvasWrapRef} className="lg:col-span-3 border border-border/60 rounded-lg overflow-auto bg-white p-2" style={{ maxHeight: '75vh' }}>
              <EendraadCanvas ref={eendraadRef} tree={schema.tree} meta={meta} signature={schema.signature?.image ? schema.signature : { image: KOEN_SIGNATURE, name: KOEN_NAME, date: new Date().toISOString().slice(0, 10) }} onDeleteConsumer={deleteConsumer} onHideKwh={onHideKwh} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="situatie" forceMount className="mt-4 data-[state=inactive]:hidden">
          <SituatiePlan ref={situatieRef} situation={schema.situation} onChange={setSituation} circuits={allCircuits} meta={meta} signature={schema.signature?.image ? schema.signature : { image: KOEN_SIGNATURE, name: KOEN_NAME, date: new Date().toISOString().slice(0, 10) }} />
        </TabsContent>
      </Tabs>

      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Handtekening elektricien</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!sigImage && (
              <div className="border rounded-lg p-2 bg-white">
                <p className="text-xs text-muted-foreground mb-1">
                  {schema.signature?.image
                    ? `Huidige handtekening (${schema.signature.name || 'zonder naam'}, ${schema.signature.date ? new Date(schema.signature.date).toLocaleDateString('nl-BE') : ''})`
                    : `Standaard handtekening van ${KOEN_NAME} (staat altijd op het schema)`}
                </p>
                <img src={schema.signature?.image || KOEN_SIGNATURE} alt="handtekening" className="h-16" />
              </div>
            )}
            <SignPad onChange={setSigImage} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">Naam elektricien</label>
                <Input value={sigName} onChange={e => setSigName(e.target.value)} placeholder="Naam" data-testid="signature-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Datum</label>
                <Input type="date" value={sigDate} onChange={e => setSigDate(e.target.value)} data-testid="signature-date-input" />
              </div>
            </div>
            <div className="flex justify-between gap-2">
              {schema.signature?.image ? (
                <Button variant="outline" size="sm" onClick={removeSignature} data-testid="signature-remove-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Terug naar standaard ({KOEN_NAME})
                </Button>
              ) : <span />}
              <Button size="sm" onClick={saveSignature} data-testid="signature-save-btn">
                <PenLine className="w-4 h-4 mr-1" /> Handtekening plaatsen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
